import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllOrdersAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    clientName: '',
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();

  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllOrdersAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, filters, dispatch]);



  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      render: (text, record) => <span>{record?.orderId || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Order ID"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('orderId', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      render: (text, record) => <span>{record?.client?.name || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Client Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('clientName', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Client Mobile',
      dataIndex: 'client_mobile',
      render: (text, record) => <span>{record?.client?.phone || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Client Mobile"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('clientPhone', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Products Ordered',
      dataIndex: 'products',
      render: (text, record) =>
        record?.products?.map((x, idx) => (
          <span key={idx}>
            {x.name}
            {idx < record.products.length - 1 && ', '}
          </span>
        )),
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      render: (text, record) =>
        record?.delivery_dates?.[0]?.date
          ? moment(record.delivery_dates[0].date).format('DD-MMM-YYYY')
          : 'Not available',
    },
    {
      title: 'Delivery Status',
      dataIndex: 'delivery_status',
      render: (text, record) => (
        <Badge
          status={record?.delivery_dates?.[0]?.status?.toLowerCase()}
          text={record?.delivery_dates?.[0]?.status || 'Not available'}
        />
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('deliveryStatus', value); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Status"
          >
            <Option value="Scheduled">Scheduled</Option>
            <Option value="Delivered">Delivered</Option>
            <Option value="Pending">Pending</Option>
            <Option value="Skipped">Skipped</Option>
          </Select>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Payment Method',
      dataIndex: 'payment_method',
      render: (text) => <span>{text || 'Not available'}</span>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (text) => <span>{text || 'Not available'}</span>,
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            clientName: '',
            deliveryDateRange: [],
            deliveryStatus: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 20 });

          }, 1000);
        }}>Clear Filters</Button>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
